/**
 * Deterministic question templates. `analyze()` runs an ordered set of shallow
 * patterns over the question and returns a structured match describing which
 * mentions to link and how the answer variable relates to them. Intentionally
 * transparent (no ML) — the QAnswer lesson is that a handful of good templates
 * plus solid linking + ranking goes a long way.
 */
import type { QueryForm } from "../ir/types.ts";

export type PatternKind =
  | "attribute-of"
  | "possessive"
  | "active-who"
  | "relation-list"
  | "count-of"
  | "count-class"
  | "superlative";

export interface PatternMatch {
  kind: PatternKind;
  template: string;
  form: QueryForm;
  entityMention?: string;
  /** A relation phrase to run through the RelationLinker (lexicon-first). */
  relationPhrase?: string;
  classNoun?: string;
  superlativeAdj?: string;
  /** Where the linked entity sits in the triple; the answer takes the other slot. */
  entityRole: "subject" | "object";
}

/** who-verb → a relation phrase that exists in the curated lexicon. */
const ACTIVE_VERB_TO_PHRASE: Record<string, string> = {
  directed: "directed by",
  wrote: "author",
  authored: "author",
  composed: "composer",
  produced: "producer",
  founded: "founder",
};

const RELATION_LIST_CONNECTORS =
  "directed by|written by|composed by|produced by|created by|discovered by|painted by|designed by|starring|featuring";

function clean(question: string): string {
  return question.trim().replace(/[?!.]+$/g, "").replace(/\s+/g, " ").trim();
}

function stripLead(text: string): string {
  let out = text;
  const lead =
    /^(what(?:'s| is| are| was| were)?|who(?:'s| is| are| was)?|which|please|can you|could you|tell me|give me|show me|list|find|name|do you know)\s+/i;
  // Peel repeated leading question/politeness words.
  for (let i = 0; i < 4 && lead.test(out); i++) out = out.replace(lead, "");
  return out.trim();
}

function stripArticles(text: string): string {
  return text.replace(/^(the|a|an|all|any)\s+/i, "").trim();
}

/** Returns the best structural match, or null if no template fires. */
export function analyze(question: string): PatternMatch | null {
  const cleaned = clean(question);

  // 1. count-of: "how many <rel> did/does <entity> have"
  let m = cleaned.match(/^how many (.+?) (?:did|does|do|has|have|had) (.+?) (?:have|got)$/i);
  if (m && m[1] && m[2]) {
    return {
      kind: "count-of",
      template: "how many <relation> did <entity> have",
      form: "count",
      relationPhrase: stripArticles(m[1]),
      entityMention: m[2].trim(),
      entityRole: "subject",
    };
  }

  // 2. active-who: "who directed <entity>"
  m = cleaned.match(/^who (directed|wrote|authored|composed|produced|founded) (.+)$/i);
  if (m && m[1] && m[2]) {
    const phrase = ACTIVE_VERB_TO_PHRASE[m[1].toLowerCase()];
    if (phrase) {
      return {
        kind: "active-who",
        template: "who <verb> <entity>",
        form: "select",
        relationPhrase: phrase,
        entityMention: m[2].trim(),
        entityRole: "subject",
      };
    }
  }

  // 3. relation-list: "<class> <connector> <entity>"  e.g. "films directed by Nolan"
  const listRe = new RegExp(`^(.+?) (${RELATION_LIST_CONNECTORS}) (.+)$`, "i");
  m = stripLead(cleaned).match(listRe);
  if (m && m[1] && m[2] && m[3]) {
    return {
      kind: "relation-list",
      template: "<class> <relation> <entity>",
      form: "select",
      classNoun: stripArticles(m[1]),
      relationPhrase: m[2].toLowerCase(),
      entityMention: m[3].trim(),
      entityRole: "object",
    };
  }

  // 4. superlative: "<superlative> <class>"  e.g. "tallest mountain"
  const lead = stripLead(cleaned);
  m = lead.match(
    /^(largest|biggest|smallest|most populous|most populated|least populous|tallest|highest|lowest|longest|shortest|oldest|newest)\s+(.+)$/i,
  );
  if (m && m[1] && m[2]) {
    // Trim a trailing qualifier ("... in the world") — the class is the head noun.
    const classNoun = stripArticles(m[2].split(/\b(?:in|on|of)\b/i)[0] ?? m[2]);
    return {
      kind: "superlative",
      template: "<superlative> <class>",
      form: "select",
      superlativeAdj: m[1].toLowerCase(),
      classNoun,
      entityRole: "subject",
    };
  }

  // 5. count-class: "how many <class>"
  m = cleaned.match(/^how many (.+?)(?: are there| exist| are known| are there in total)?$/i);
  if (m && m[1]) {
    return {
      kind: "count-class",
      template: "how many <class>",
      form: "count",
      classNoun: stripArticles(m[1]),
      entityRole: "subject",
    };
  }

  // 6. possessive: "<entity>'s <relation>"  e.g. "France's capital"
  m = lead.match(/^(.+?)'s (.+)$/i);
  if (m && m[1] && m[2]) {
    return {
      kind: "possessive",
      template: "<entity>'s <relation>",
      form: "select",
      entityMention: m[1].trim(),
      relationPhrase: stripArticles(m[2]),
      entityRole: "subject",
    };
  }

  // 7. attribute-of: "<relation> of <entity>"  e.g. "capital of France"
  m = lead.match(/^(.+?) of (.+)$/i);
  if (m && m[1] && m[2]) {
    return {
      kind: "attribute-of",
      template: "<relation> of <entity>",
      form: "select",
      relationPhrase: stripArticles(m[1]),
      entityMention: m[2].trim(),
      entityRole: "subject",
    };
  }

  return null;
}
